/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Button, Typography, useTheme } from '@mui/material';
import React from 'react';
import { FilterChip } from '@opengeoweb/shared';
import { breakpoints } from '@opengeoweb/theme';
import { useWarningsTranslation } from '../../../utils/i18n';
import type { DistrictsType } from '../types';
import { useFilterChipSelect } from '../useFilterChipSelect';

export interface AssignedAreasProps {
  assignedAreas: DistrictsType[];
  selectedAreaIds: string[];
  onAddClick: () => void;
  onDeleteClick: (id: string) => void;
  onToggleSelectArea: (id: string) => void;
  onSelectMultipleAreas: (id: string[]) => void;
  shouldShowDistricts?: boolean;
  isReadOnly?: boolean;
}

export const AssignedAreas: React.FC<AssignedAreasProps> = ({
  assignedAreas,
  selectedAreaIds,
  onAddClick,
  onDeleteClick,
  onToggleSelectArea,
  onSelectMultipleAreas,
  isReadOnly = false,
  shouldShowDistricts = false,
}: AssignedAreasProps) => {
  const { t } = useWarningsTranslation();

  const assignedAreaIds = React.useMemo(() => {
    return new Set(assignedAreas.map((area) => area.districtName));
  }, [assignedAreas]);

  // can add the selected areas when they don't appear in the assigned area list
  const canAddSelectedAreas = React.useMemo(() => {
    return (
      !isReadOnly &&
      selectedAreaIds.length > 0 &&
      selectedAreaIds.some((id) => !assignedAreaIds.has(id))
    );
  }, [isReadOnly, selectedAreaIds, assignedAreaIds]);

  const assignedAreaIdsArray = React.useMemo(
    () => Array.from(assignedAreaIds),
    [assignedAreaIds],
  );

  const { handleChipClick } = useFilterChipSelect({
    selectedIds: selectedAreaIds,
    assignedIds: assignedAreaIdsArray,
    onSelectMultiple: onSelectMultipleAreas,
    onToggleSelect: onToggleSelectArea,
  });

  const theme = useTheme();

  return (
    <Box
      style={{
        backgroundColor: canAddSelectedAreas
          ? theme.palette.geowebColors.cards.cardContainerMouseOver
          : undefined,
      }}
      sx={{
        display: 'flex',
        gridArea: 'areas',
        gap: 1,
        minHeight: 32,
        paddingInline: 0.5,
        borderRadius: 1,

        [`@container maritime (min-width: ${breakpoints.tablet}px)`]: {
          marginLeft: 1,
        },
      }}
    >
      {assignedAreas.length === 0 && (
        <Typography
          color="textSecondary"
          sx={{
            alignSelf: 'center',
          }}
          variant="caption"
        >
          {shouldShowDistricts
            ? t('maritime-no-district-selected')
            : t('maritime-no-area-selected')}
        </Typography>
      )}

      <Box
        style={{
          pointerEvents: !isReadOnly ? 'auto' : 'none',
        }}
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          paddingBlock: 0.5,
          alignItems: 'flex-start',

          [`@container maritime (min-width: ${breakpoints.tablet}px)`]: {
            flexWrap: 'wrap',
            flexDirection: 'row',
          },
        }}
      >
        {assignedAreas.map((area) => {
          const isSelected = selectedAreaIds.includes(area.districtName);

          return (
            <FilterChip
              id={area.districtName}
              isSelected={isSelected}
              key={area.districtName}
              label={area.districtName}
              onClick={(_chipRef, event): void => {
                if (!isReadOnly) {
                  handleChipClick(area.districtName, event);
                }
              }}
              onClickIcon={
                isSelected && !isReadOnly
                  ? (): void => {
                      onDeleteClick(area.districtName);
                    }
                  : undefined
              }
              size="small"
            />
          );
        })}
      </Box>

      {!isReadOnly && (
        <AddButton onClick={onAddClick} show={canAddSelectedAreas} />
      )}
    </Box>
  );
};

interface AddButtonProps {
  onClick: () => void;
  show: boolean;
}

const AddButton: React.FC<AddButtonProps> = ({
  onClick,
  show,
}: AddButtonProps) => {
  const { t } = useWarningsTranslation();
  return (
    <Button
      onClick={onClick}
      style={{
        visibility: show ? 'visible' : 'hidden',
      }}
      sx={(theme) => ({
        alignSelf: 'flex-start',
        height: 16,
        minWidth: 0,
        flexShrink: 0,
        marginTop: 0.5,
        ...theme.typography.caption,
      })}
      variant="primary"
    >
      {t('maritime-add-area')}
    </Button>
  );
};
